import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { openMailtoFromTemplate } from "@/lib/emailTemplates";
import { CONSOLIDATION } from "@/constants/testIds";
import {
  Bell,
  Eye,
  Play,
  Mail,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";

function Stat({ label, value }) {
  return (
    <div className="border border-[#e8e1cd] bg-[#fbf7ee] p-2 rounded">
      <div className="text-[9px] uppercase tracking-widest text-[#8b7e5e]">
        {label}
      </div>
      <div className="text-[12px] text-[#1c1a14] truncate" title={value}>
        {value}
      </div>
    </div>
  );
}

export default function ReminderSettingsSection() {
  const [enabled, setEnabled] = useState(false);
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [originalPhone, setOriginalPhone] = useState("");
  const [originalEmail, setOriginalEmail] = useState("");
  const [originalEnabled, setOriginalEnabled] = useState(false);
  const [lastRunAt, setLastRunAt] = useState(null);
  const [lastSent, setLastSent] = useState(0);
  const [lastError, setLastError] = useState("");
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState("");
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/consolidation/reminder-settings");
      setEnabled(!!data.enabled);
      setOriginalEnabled(!!data.enabled);
      setPhone(data.coordinator_phone || "");
      setOriginalPhone(data.coordinator_phone || "");
      setEmail(data.coordinator_email || "");
      setOriginalEmail(data.coordinator_email || "");
      setLastRunAt(data.last_run_at);
      setLastSent(data.last_sent_count || 0);
      setLastError(data.last_error || "");
      setConfigured(!!data.configured);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error cargando configuración");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    queueMicrotask(load);
  }, []);

  const showInfo = (msg) => {
    setInfo(msg);
    setError("");
    setTimeout(() => setInfo(""), 3500);
  };

  const save = async () => {
    setError("");
    setSaving(true);
    try {
      const { data } = await api.put("/consolidation/reminder-settings", {
        enabled,
        coordinator_phone: phone,
        coordinator_email: email,
      });
      setOriginalPhone(data.coordinator_phone || "");
      setOriginalEmail(data.coordinator_email || "");
      setOriginalEnabled(!!data.enabled);
      setPhone(data.coordinator_phone || "");
      setEmail(data.coordinator_email || "");
      showInfo("Configuración guardada");
    } catch (e) {
      setError(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async () => {
    setError("");
    setPreview(null);
    try {
      const { data } = await api.get("/consolidation/reminder-preview");
      setPreview(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error en preview");
    }
  };

  const runNow = async () => {
    setError("");
    try {
      const { data } = await api.post("/consolidation/reminder-run-now");
      showInfo(data.detail || "Ejecutado");
      await load();
      setPreview(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error al ejecutar");
    }
  };

  const sendEmailReminder = async () => {
    setError("");
    try {
      const { data } = await api.get("/consolidation/reminder-preview");
      if (!data.eligible || data.eligible.length === 0) {
        setError("No hay visitantes elegibles para enviar recordatorio en este momento.");
        return;
      }
      if (!email) {
        setError("Configura primero el email del coordinador pastoral.");
        return;
      }
      const lines = data.eligible
        .map(
          (p) =>
            `  • ${p.full_name} — ${p.phone || "sin teléfono"} — visitó ${p.first_visit_date}`,
        )
        .join("\n");
      await openMailtoFromTemplate(
        "reminder_48h",
        email,
        {
          cantidad: data.eligible.length,
          lista_visitantes: lines,
          fecha_hoy: new Date().toISOString().slice(0, 10),
        },
        {
          context_label: `Recordatorio 48h — ${data.eligible.length} visitante(s)`,
        },
      );
    } catch (e) {
      setError(e?.response?.data?.detail || "Error al preparar email");
    }
  };

  if (loading) return null;
  const dirty =
    phone !== originalPhone ||
    email !== originalEmail ||
    enabled !== originalEnabled;

  return (
    <section
      data-testid={CONSOLIDATION.reminderSection}
      className="mt-8 border border-[#d9cfb8] bg-white p-5 shadow-sm"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 text-[#1c1a14]">
            <Bell size={16} className="text-[#c8a04a]" />
            <h3 className="font-serif text-base">
              Recordatorio pastoral 48h (seguimiento a visitantes)
            </h3>
          </div>
          <p className="text-[11px] text-[#5d5849] mt-0.5">
            Visitantes registrados hace 48–72h sin seguimiento. Envía el recordatorio
            por <strong>email</strong> (manual, sin costos) o por <strong>SMS automático</strong>.
          </p>
        </div>
        <span
          className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded ${
            configured
              ? "bg-emerald-100 text-emerald-700 border border-emerald-300"
              : "bg-amber-100 text-amber-800 border border-amber-300"
          }`}
        >
          {configured ? "Twilio activo" : "Modo simulación"}
        </span>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <label className="flex items-center gap-2 self-end pb-2">
          <input
            data-testid={CONSOLIDATION.reminderEnableToggle}
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="h-4 w-4 accent-[#c8a04a]"
          />
          <span className="text-[12px] text-[#1c1a14]">Activar recordatorios cada hora</span>
        </label>

        <label className="block">
          <span className="block text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-1">
            Teléfono del coordinador (SMS)
          </span>
          <input
            data-testid={CONSOLIDATION.reminderPhoneInput}
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="ej. +19253502697"
            className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded focus:border-[#c8a04a] focus:outline-none"
          />
        </label>

        <label className="block">
          <span className="block text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-1">
            Email del coordinador (envío manual)
          </span>
          <input
            data-testid="reminder-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="coordinador@iglesia.com"
            className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded focus:border-[#c8a04a] focus:outline-none"
          />
        </label>
      </div>

      {error && (
        <div
          data-testid={CONSOLIDATION.reminderErrorMsg}
          className="mt-3 flex items-center gap-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-700"
        >
          <AlertCircle size={14} /> {error}
        </div>
      )}
      {info && !error && (
        <div
          data-testid={CONSOLIDATION.reminderInfoMsg}
          className="mt-3 flex items-center gap-2 rounded border border-emerald-300 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-700"
        >
          <CheckCircle2 size={14} /> {info}
        </div>
      )}

      {/* Stats */}
      <div
        data-testid={CONSOLIDATION.reminderStats}
        className="mt-4 grid grid-cols-3 gap-3 text-[11px]"
      >
        <Stat label="Última ejecución" value={lastRunAt ? new Date(lastRunAt).toLocaleString() : "—"} />
        <Stat label="Enviados en última corrida" value={lastSent} />
        <Stat label="Último error" value={lastError || "—"} />
      </div>

      {/* Preview eligible list */}
      {preview && (
        <div className="mt-4 border border-[#e8e1cd] bg-[#fbf7ee] p-3 text-[12px]">
          <div className="text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-1">
            Vista previa ({preview.count} visitante(s) elegibles)
          </div>
          {preview.eligible?.length ? (
            <ul className="space-y-1">
              {preview.eligible.map((p) => (
                <li key={p.id} className="text-[#1c1a14]">
                  • {p.full_name} — {p.phone || "sin teléfono"} — {p.first_visit_date}
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-[#5d5849] italic">{preview.detail}</div>
          )}
        </div>
      )}

      <div className="mt-4 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            data-testid={CONSOLIDATION.reminderPreviewButton}
            onClick={runPreview}
            className="inline-flex items-center gap-1 rounded border border-[#1c2a3a] px-3 py-1.5 text-[11px] uppercase tracking-widest text-[#1c2a3a] hover:bg-[#fbf7ee]"
          >
            <Eye size={12} /> Vista previa
          </button>
          <button
            type="button"
            data-testid="reminder-email-button"
            onClick={sendEmailReminder}
            className="inline-flex items-center gap-1 rounded border border-[#1c2a3a] bg-[#0f1a2c] px-3 py-1.5 text-[11px] uppercase tracking-widest text-[#e7c879] hover:bg-[#1c2a3a]"
            title="Abre tu cliente de email con el recordatorio prellenado"
          >
            <Mail size={12} /> Enviar email
          </button>
          <button
            type="button"
            data-testid={CONSOLIDATION.reminderRunNowButton}
            onClick={runNow}
            className="inline-flex items-center gap-1 rounded border border-[#0f7a4a] bg-[#0f7a4a] px-3 py-1.5 text-[11px] uppercase tracking-widest text-white hover:bg-[#0a5a35]"
          >
            <Play size={12} /> Ejecutar SMS
          </button>
        </div>
        <button
          type="button"
          data-testid={CONSOLIDATION.reminderSaveButton}
          onClick={save}
          disabled={!dirty || saving}
          className="rounded border border-[#c8a04a] bg-[#c8a04a] px-3 py-1.5 text-[11px] uppercase tracking-widest text-[#1c1a14] hover:bg-[#b8902a] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving ? "Guardando..." : "Guardar configuración"}
        </button>
      </div>
    </section>
  );
}
